import localForage from "localforage";
import CryptoJS from "crypto-js";
import { EncryptedBadge, ScannedBadge } from "../../redux/types";
import { IPersonBadge, Nullable, DecryptBadgeError, DecryptBadgeResponse, IPersonBadgeShortened } from "../types";
import dayjs from "dayjs";
import { BADGE_FILTERING_DATE_FORMAT, FREYA_SCAN_POSSIBLE_AFTER_X_MINUTES } from "../constants";
import { BadgeStatuses, ReasonCodes } from "../enums";

const groupedBadgesKey = "freyaBadges";
const scannedBadgesKey = "freyaScannedBadges";
const npsPrintCountKey = "freyaNpsPrintCount";
const npsSurveyedBadgesKey = "freyaNpsSurveyedBadges";

const getBadges = async () => {
  const fetchedBadges = (await localForage.getItem(groupedBadgesKey)) as EncryptedBadge[];
  return fetchedBadges ? fetchedBadges : [];
};

const deleteBadges = async () => {
  try {
    await localForage.removeItem(groupedBadgesKey);
  } catch (e) {
    console.warn(e);
  }
};

const getEncryptedBadgeById = async (id: string) => {
  const badgesList = await getBadges();
  return badgesList.find(badge => badge.badgeId === id);
};

const isBadgeActive = (badgeStatus: string | undefined) => {
  return badgeStatus === BadgeStatuses.ACTIVE;
};

const isCurrentVerion = (foundBadgeVersion: number | undefined, scannedBadgeVersion: number) => {
  return foundBadgeVersion === scannedBadgeVersion;
};

// FOR TESTING
const encryptBadge = (badge: IPersonBadge, id: string, passphrase) => {
  const iv = id.substring(0, 16);
  const cryptkey = CryptoJS.enc.Utf8.parse(passphrase);
  const badgeBinary = CryptoJS.enc.Utf8.parse(JSON.stringify(badge));
  let decrypt = CryptoJS.AES.encrypt(badgeBinary, cryptkey, {
    iv: CryptoJS.enc.Utf8.parse(iv),
    mode: CryptoJS.mode.CBC
  });
  return CryptoJS.enc.Base64.stringify(decrypt.ciphertext);
};

const decryptBadge = (badge: EncryptedBadge, id: string, passphrase: string) => {
  const iv = id.substring(0, 16);
  const cryptkey = CryptoJS.enc.Utf8.parse(passphrase);
  let decrypt = CryptoJS.AES.decrypt(badge.badgeData, cryptkey, {
    iv: CryptoJS.enc.Utf8.parse(iv),
    mode: CryptoJS.mode.CBC
  });
  return CryptoJS.enc.Utf8.stringify(decrypt);
};

const getDecryptedBadge = async (id: string, passphrase: string, badgeVersion: number, scannedQrCode: string) => {
  try {
    let errors: DecryptBadgeError[] = [];
    let decryptedBadge: Nullable<IPersonBadge | IPersonBadgeShortened> = null;
    const response: DecryptBadgeResponse = { decryptedBadge, errors };

    let decrypted: Nullable<string> = null;
    const badge = await getEncryptedBadgeById(id);

    if (!badge?.badgeData) {
      return { decryptedBadge: null, errors: [{ reasonCode: ReasonCodes.BadgeNotFound, reasonCodeValues: {} }] };
    }

    if (badge && !isBadgeActive(badge.status)) {
      errors.push({
        reasonCode: ReasonCodes.InvalidStatus,
        reasonCodeValues: { currentBadgeStatus: badge.status }
      });
      return response;
    }

    if (badge && !isCurrentVerion(badge.v, badgeVersion)) {
      errors.push({
        reasonCode: ReasonCodes.InvalidVersion,
        reasonCodeValues: { expectedBadgeVersion: badge.v.toString(), scannedBadgeVersion: badgeVersion.toString() }
      });
      return response;
    }

    decrypted = decryptBadge(badge, id, passphrase);

    if (!decrypted) {
      return {
        decryptedBadge: null,
        errors: [{ reasonCode: ReasonCodes.UnableToDecryptBadge, reasonCodeValues: { scannedQrCode } }]
      };
    }

    response.decryptedBadge = JSON.parse(decrypted);
    return response;
  } catch (e) {
    return {
      decryptedBadge: null,
      errors: [{ reasonCode: ReasonCodes.UnableToDecryptBadge, reasonCodeValues: { scannedQrCode } }]
    };
  }
};

const setBadges = async (badges: EncryptedBadge[]) => {
  await localForage.setItem(groupedBadgesKey, badges);
};

const getScannedBadges = async () => {
  const scannedBadges = (await localForage.getItem(scannedBadgesKey)) as ScannedBadge[];
  return scannedBadges || [];
};

const addScannedBadge = async (badge: IPersonBadge, scannedBadges: ScannedBadge[], nowDate: Date) => {
  await localForage.setItem(scannedBadgesKey, [
    ...scannedBadges,
    { badgeId: badge.id, date: dayjs(nowDate).format(BADGE_FILTERING_DATE_FORMAT) }
  ]);
};

const clearScannedBadgesOlderThanXMinutes = async () => {
  const scannedBadges = await getScannedBadges();
  const now = new Date();
  const filteredBadges = scannedBadges.filter(badge =>
    dayjs(now).subtract(FREYA_SCAN_POSSIBLE_AFTER_X_MINUTES, "minutes").isBefore(dayjs(badge.date))
  );
  await localForage.setItem(scannedBadgesKey, filteredBadges);
};

const deleteScannedBadges = async () => {
  try {
    await localForage.removeItem(scannedBadgesKey);
  } catch (e) {
    console.warn(e);
  }
};

const getNpsPrintCount = async () => {
  const count = (await localForage.getItem(npsPrintCountKey)) as number;
  return count || 0;
};

const incrementNpsPrintCount = async () => {
  const newCount = (await getNpsPrintCount()) + 1;
  await localForage.setItem(npsPrintCountKey, newCount);
  return newCount;
};

const getNpsSurveyedBadgeIds = async () => {
  const badgeIds = (await localForage.getItem(npsSurveyedBadgesKey)) as string[];
  return badgeIds || [];
};

const addNpsSurveyedBadgeId = async (badgeId: string) => {
  const badgeIds = await getNpsSurveyedBadgeIds();
  if (!badgeIds.includes(badgeId)) {
    await localForage.setItem(npsSurveyedBadgesKey, [...badgeIds, badgeId]);
  }
};

const FreyaStorage = {
  getBadges,
  deleteBadges,
  getDecryptedBadge,
  setBadges,
  encryptBadge,
  decryptBadge,
  getScannedBadges,
  addScannedBadge,
  clearScannedBadgesOlderThanXMinutes,
  deleteScannedBadges,
  getNpsPrintCount,
  incrementNpsPrintCount,
  getNpsSurveyedBadgeIds,
  addNpsSurveyedBadgeId
};

export default FreyaStorage;
